import React, {useEffect} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {CustomerDialogButtonRow, H5, Row, Col} from '@modifi/ui'
import {Trans, useTranslation} from '@modifi/plugin-i18n'

import {EventTimeline, ProgressBar} from './components'
import {Container, TextCenter} from './styles'

const propTypes = {
  params: PropTypes.shape({
    externalShipmentId: PropTypes.string.isRequired,
  }).isRequired,
  hasError: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  fetchDetails: PropTypes.func.isRequired,
  clearDetails: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  shipmentDetails: PropTypes.shape({
    shipment: PropTypes.shape({
      shippingStartDate: PropTypes.string,
      estimatedShippingStartDate: PropTypes.string,
      shippingEndDate: PropTypes.string,
      estimatedShippingEndDate: PropTypes.string,
      origin: PropTypes.shape({
        countryCode: PropTypes.string,
        name: PropTypes.string,
      }),
      destination: PropTypes.shape({
        countryCode: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
    containerEventViews: PropTypes.arrayOf(
      PropTypes.shape({
        location: PropTypes.shape({
          name: PropTypes.string,
          countryCode: PropTypes.string,
        }),
        destination: PropTypes.shape({
          name: PropTypes.string,
          countryCode: PropTypes.string,
        }),
        events: PropTypes.arrayOf(
          PropTypes.shape({
            code: PropTypes.string,
            date: PropTypes.string,
            isEstimated: PropTypes.bool,
            vesselNumber: PropTypes.string,
          }).isRequired
        ),
      }).isRequired
    ),
  }),
  closeDialog: PropTypes.func.isRequired,
}
const defaultProps = {
  shipmentDetails: undefined as undefined | null,
}

export type ShipmentTrackingDetailsProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const ShipmentTrackingDetails: React.FC<ShipmentTrackingDetailsProps> = ({
  hasError,
  isLoading,
  params,
  fetchDetails,
  clearDetails,
  shipmentDetails,
  closeDialog,
}) => {
  const {t} = useTranslation('argus')

  const {externalShipmentId} = params

  useEffect(() => {
    fetchDetails(externalShipmentId)
  }, [fetchDetails, externalShipmentId])

  useEffect(
    () => () => {
      clearDetails()
    },
    [clearDetails]
  )

  useEffect(() => {
    if (hasError) {
      closeDialog()
    }
  }, [closeDialog, hasError])

  return (
    <Container>
      <TextCenter>
        <H5 mt={hasError ? 0 : 30} mb={40}>
          <Trans
            i18nKey="shipment-tracking-details.dialog.title"
            t={t}
            values={{
              shipmentId: params?.externalShipmentId,
            }}
          />
        </H5>
      </TextCenter>
      <Row>
        <Col>
          <ProgressBar
            isLoading={isLoading || !shipmentDetails?.shipment}
            shipment={shipmentDetails?.shipment ?? undefined}
          />
        </Col>
        <Col mt={64}>
          <EventTimeline
            isLoading={isLoading}
            t={t}
            containerEvents={shipmentDetails?.containerEventViews ?? undefined}
          />
        </Col>
      </Row>
      <CustomerDialogButtonRow modal submitText={t('cta.close')} onSubmitClick={closeDialog} />
    </Container>
  )
}

ShipmentTrackingDetails.propTypes = propTypes
ShipmentTrackingDetails.defaultProps = defaultProps

export default ShipmentTrackingDetails
